import { useEffect, useState } from 'react';

import { api } from './lib/api.js';
import { LanguageProvider, useI18n } from './lib/i18n.jsx';
import { PARENT_SECTIONS, PUBLIC_PATHS, useLocation } from './lib/router.js';
import { Dashboard } from './pages/Dashboard.jsx';
import { Login } from './pages/Login.jsx';
import { ParentRegister } from './pages/ParentRegister.jsx';
import { VerifyEmail } from './pages/VerifyEmail.jsx';

/**
 * Portal shell and routing.
 *
 * Which surface renders is decided by what the server returns from /auth/me,
 * never by anything the client stored, so nothing in the browser can grant
 * access. The router only chooses which screen of an already-authorised
 * surface is shown.
 */
/**
 * Wraps the app in the language provider.
 *
 * The provider needs the signed-in parent's stored preference, and the session
 * is loaded inside App, so the two are joined here: App reports the profile
 * language up, and the provider decides whether to adopt it.
 */
export function App() {
  const [profileLanguage, setProfileLanguage] = useState(null);

  return (
    <LanguageProvider
      profileLanguage={profileLanguage}
      onPersist={(language) => {
        // Best effort: an anonymous visitor has no profile to write to, and a
        // failed save must not undo a switch the user can already see.
        api.updateMe({ language }).catch(() => {});
      }}
    >
      <Routes onProfileLanguage={setProfileLanguage} />
    </LanguageProvider>
  );
}

function Routes({ onProfileLanguage }) {
  const { t } = useI18n();
  const { path, search, navigate, replace } = useLocation();
  const [session, setSession] = useState({ status: 'loading', type: null, user: null });

  async function loadSession() {
    try {
      const result = await api.me();
      setSession({ status: 'signed-in', type: result.type, user: result.user });
      onProfileLanguage?.(result.user?.language ?? null);
      return result;
    } catch {
      setSession({ status: 'anonymous', type: null, user: null });
      return null;
    }
  }

  useEffect(() => {
    loadSession();
  }, []);

  // Redirects run in an effect rather than during render, so history is only
  // touched once the session is actually known.
  useEffect(() => {
    if (session.status === 'loading') return;

    const isPublic = PUBLIC_PATHS.includes(path);

    if (session.status === 'anonymous' && !isPublic) {
      replace('/login');
      return;
    }

    // A signed-in parent has no business on the sign-in screen. replace, not
    // push, so Back does not bounce them straight back out.
    if (session.status === 'signed-in' && (path === '/login' || path === '/register')) {
      replace('/');
    }
  }, [session.status, path, replace]);

  async function signOut() {
    await api.logout().catch(() => {});
    setSession({ status: 'anonymous', type: null, user: null });
    navigate('/login');
  }

  // Verification comes before everything, including the signed-in surface: a
  // parent may click the link while already signed in on this device, and that
  // click still has to consume the token.
  if (path === '/verify') {
    return (
      <div data-surface="parent">
        <VerifyEmail
          onGoToLogin={async () => {
            await loadSession();
            navigate('/');
          }}
        />
      </div>
    );
  }

  if (session.status === 'loading') {
    return (
      <div className="center-page">
        <p aria-live="polite">{t('common.loading')}</p>
      </div>
    );
  }

  if (session.status === 'signed-in') {
    if (session.type === 'parent') {
      const section = PARENT_SECTIONS.find((item) => item.path === path)?.id ?? 'overview';

      return (
        <Dashboard
          user={session.user}
          section={section}
          search={search}
          onNavigate={navigate}
          onSignOut={signOut}
        />
      );
    }

    // The student surface is a structured lesson, never an open chat.
    return (
      <div data-surface="student" className="center-page">
        <main className="card card--auth stack">
          <h1>{t('student.welcome')}</h1>
          <p>{t('student.noLesson')}</p>
          <button className="button button--secondary" type="button" onClick={signOut}>
            {t('common.signOut')}
          </button>
        </main>
      </div>
    );
  }

  if (path === '/register') {
    return (
      <div data-surface="parent">
        <ParentRegister onGoToLogin={() => navigate('/login')} />
      </div>
    );
  }

  return (
    <div data-surface="parent">
      <Login
        onSignedIn={async () => {
          await loadSession();
          navigate('/');
        }}
        onGoToRegister={() => navigate('/register')}
      />
    </div>
  );
}
